import { createSelector } from "@reduxjs/toolkit";
import type { IDevice } from "@multiconsult-group/multiplacer-core";
import type { RoomSectionRenderItem } from "@multiconsult-group/multiplacer-ui";
import type { IRootState } from "../app/store";
import { selectHttpDevices } from "../DesignTab/devices/deviceSelectors";
import { selectHttpGetMultiplacerDeviceTypesData, selectNonPublishedDevices, NON_PUBLISHED_DEVICE_OPACITY } from "../DesignTab/devices/deviceSlice";
import { convertSingleDeviceToPixPolygon } from "../DesignTab/devices/deviceUtils";
import { selectHttpMcbimSpacesResult } from "../McBim/httpMcBimSelectors";
import { createDeviceCeilingHeightResolver, type CeilingHeightSpace } from "./deviceCeilingHeight";

export const CEILING_DEVICE_3D_ID_PREFIX = "ceiling-device:";
// Devices have a 2D footprint only. Show a shallow solid above their bottom edge.
const DEVICE_DEPTH_M = 0.2;

// McBim buildingStory.elevation is stored in millimeters; OBB geometry uses meters.
export const selectStreetViewFloorElevationM = (state: IRootState): number | undefined => {
  const elevationMm = state.design.floorAndJob?.elevation;
  return elevationMm != null && Number.isFinite(elevationMm) ? elevationMm / 1000 : undefined;
};

export const buildCeilingDevice3dItems = (
  devices: IDevice[],
  deviceTypes: ReturnType<typeof selectHttpGetMultiplacerDeviceTypesData>,
  floorElevationM: number | undefined,
  selectedDeviceId: string | null,
  showTrueSize = false,
  spaces: CeilingHeightSpace[] = []
): RoomSectionRenderItem[] => {
  const getCeilingHeight = createDeviceCeilingHeightResolver(spaces);
  return devices.flatMap((device) => {
    if (
      device._id == null ||
      device.coordinate?.length < 2 ||
      !device.coordinate?.slice(0, 2).every(Number.isFinite)
    )
      return [];
    const direction = device.direction;
    const polygon = convertSingleDeviceToPixPolygon({
      deviceItem: {
        ...device,
        direction:
          direction?.every(Number.isFinite) && Math.hypot(...direction) > 0 ? direction : [1, 0],
      },
      typeDevices: deviceTypes,
      selectedDeviceId,
      showTrueSize,
      isMoveable: false,
    });
    if (
      !polygon ||
      polygon.points.length < 4 ||
      !polygon.points.every((p) => p.every(Number.isFinite))
    )
      return [];
    const [a, b, , d] = polygon.points;
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const width = Math.hypot(d[0] - a[0], d[1] - a[1]);
    // Reconstruct from the canonical floor-relative height. Older stored coordinate[2]
    // values were created by adding the floor elevation in mm to a height in meters.
    const bottomZ =
      getCeilingHeight(device) ??
      (floorElevationM != null
        ? floorElevationM + device.bottomEdgeHeightAboveFloorMm / 1000
        : device.coordinate[2]);
    if (!(length > 0 && width > 0) || !Number.isFinite(bottomZ)) return [];
    const selected = device._id === selectedDeviceId;
    return [
      {
        item: {
          _id: `${CEILING_DEVICE_3D_ID_PREFIX}${device._id}`,
          name: device.compCode,
          geometry: {
            Obb3DWorldMmList: [
              {
                centerM: {
                  x: (b[0] + d[0]) / 2,
                  y: (b[1] + d[1]) / 2,
                  z: bottomZ + DEVICE_DEPTH_M / 2,
                },
                axisA: { x: (b[0] - a[0]) / length, y: (b[1] - a[1]) / length, z: 0 },
                axisB: { x: (d[0] - a[0]) / width, y: (d[1] - a[1]) / width, z: 0 },
                axisC: { x: 0, y: 0, z: 1 },
                extentAM: length / 2,
                extentBM: width / 2,
                extentCM: DEVICE_DEPTH_M / 2,
              },
            ],
          },
        },
        color: selected ? "#dc4242" : (polygon.fillColor ?? "#000000"),
        opacity: 1,
        outline: { color: selected ? "#dc4242" : "#000000", opacity: 1, width: selected ? 4 : 1 },
      },
    ];
  });
};

export const selectCeilingDevice3dItems = createSelector(
  [
    selectHttpDevices,
    selectHttpGetMultiplacerDeviceTypesData,
    selectStreetViewFloorElevationM,
    (state: IRootState) => state.device.selectedDeviceId,
    (state: IRootState) => state.design.hideDevices,
    (state: IRootState) => state.design.showTrueSizeOfDevices,
    selectHttpMcbimSpacesResult,
  ],
  (devices, types, elevation, selectedId, hideDevices, showTrueSize, spaces = []) =>
    hideDevices
      ? []
      : buildCeilingDevice3dItems(devices, types, elevation, selectedId, showTrueSize, spaces)
);

export const PREVIEW_DEVICE_3D_ID_PREFIX = "preview-device:";

export const selectNonPublishedDevice3dItems = createSelector(
  [selectNonPublishedDevices, selectHttpGetMultiplacerDeviceTypesData,
    selectStreetViewFloorElevationM,
    (state: IRootState) => state.design.showTrueSizeOfDevices,
    selectHttpMcbimSpacesResult],
  (devices, types, elevation, showTrueSize, spaces) =>
    buildCeilingDevice3dItems(
      devices.map((device, index) => ({ ...device, _id: device._id ?? `draft-${index}` })),
      types, elevation, null, showTrueSize, spaces
    ).map((rendered) => ({
      ...rendered,
      item: { ...rendered.item, _id: `${PREVIEW_DEVICE_3D_ID_PREFIX}${String(rendered.item._id).slice(CEILING_DEVICE_3D_ID_PREFIX.length)}` },
      opacity: NON_PUBLISHED_DEVICE_OPACITY,
      outline: rendered.outline ? { ...rendered.outline, opacity: NON_PUBLISHED_DEVICE_OPACITY } : undefined,
    }))
);
